import type { Entry, EntrySnapshot } from '@/types/domain'

/** Fields whose edit invalidates the snapshot per research.md D-11. */
export const SNAPSHOT_INPUT_FIELDS = ['dataRecebimento', 'moedaOrigem', 'valorOriginal'] as const
export type SnapshotInputField = (typeof SNAPSHOT_INPUT_FIELDS)[number]

export function emptySnapshot(): EntrySnapshot {
  return { status: 'pendente' }
}

/**
 * Returns true if the patch touches any field that is an input to the
 * calculation. If true, the caller must invalidate the snapshot and trigger
 * a recompute.
 */
export function editingInvalidatesSnapshot(patch: Partial<Entry>): boolean {
  return SNAPSHOT_INPUT_FIELDS.some((field) => Object.hasOwn(patch, field))
}

/**
 * Diff between two snapshots — returns the list of fields that changed.
 * Used by the recalculation flow to show a confirmation dialog before
 * overwriting a stored snapshot with a freshly computed one.
 */
export function diffSnapshots(a: EntrySnapshot, b: EntrySnapshot): string[] {
  const fields: (keyof EntrySnapshot)[] = [
    'status',
    'dataPTAXAplicavel',
    'cotacaoMoedaParaUSD',
    'dataCotacaoMoedaParaUSD',
    'cotacaoUSDBRL',
    'valorUSD',
    'valorBRL',
  ]
  const changed: string[] = []
  for (const field of fields) {
    if (!shallowEqual(a[field], b[field])) changed.push(field)
  }
  return changed
}

export function snapshotsDiffer(a: EntrySnapshot, b: EntrySnapshot): boolean {
  return diffSnapshots(a, b).length > 0
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== typeof b) return false
  if (typeof a === 'number' && typeof b === 'number') {
    // Compare with 6-decimal tolerance to absorb float drift on intermediate
    // values; user-facing values are rounded explicitly in the converter.
    return Math.abs(a - b) < 1e-6
  }
  return false
}
